import { Injectable } from '@angular/core';
import { Observable, of } from 'rxjs';
import { tap } from 'rxjs/operators';

interface CacheEntry<T> {
    data: T;
    expiry: number;
}

/**
 * AutocompleteCacheService provides short-lived caching for search results.
 * This prevents re-fetching the same results when the user types and deletes characters,
 * or when the same control is re-initialized quickly.
 */
@Injectable({
    providedIn: 'root'
})
export class AutocompleteCacheService {
    private cache = new Map<string, CacheEntry<any>>();
    private readonly DEFAULT_TTL = 30000; // 30 seconds TTL

    constructor() { }

    /**
     * Returns cached data if available and not expired, or null
     */
    get<T>(key: string): T | null {
        const entry = this.cache.get(key);
        if (entry && entry.expiry > Date.now()) {
            return entry.data;
        }
        if (entry) {
            this.cache.delete(key); // Cleanup expired entry
        }
        return null;
    }

    /**
     * Caches the result of an API call
     */
    set<T>(key: string, data: T, ttl: number = this.DEFAULT_TTL): void {
        this.cache.set(key, {
            data,
            expiry: Date.now() + ttl
        });
    }

    /**
     * Helper method to wrap an API call with caching
     */
    wrap<T>(cacheKey: string, apiCall: Observable<T>, ttl: number = this.DEFAULT_TTL): Observable<T> {
        const cachedData = this.get<T>(cacheKey);
        if (cachedData !== null) {
            return of(cachedData);
        }

        return apiCall.pipe(
            tap(data => this.set(cacheKey, data, ttl))
        );
    }

    /**
     * Clear search cache for a specific type (e.g., 'products')
     */
    clearType(prefix: string): void {
        for (const key of this.cache.keys()) {
            if (key.startsWith(prefix)) {
                this.cache.delete(key);
            }
        }
    }

    clear(): void {
        this.cache.clear();
    }
}
